import {
  buildTimestampAccessor,
  createDefinedStartDateWindow,
  DataRow,
  DataTable,
  dateFormatterFactory,
  dateGranularityFromTrunc,
  DateTrunc,
  DateWindow,
  DateWindowTypes,
  simpleDefinition,
} from '@tableau/metrics-engine';
import { DateInputField, DateInputFieldType } from '@tableau/widgets';
import * as React from 'react';
import { Messages } from '../../../../gen-src/Localize';

export interface DateWindowStartDateComponentProps {
  testId: string;
  data: DataTable;
  dateTrunc: DateTrunc;
  onDateWindowChange: (value: DateWindow | null) => void;
  selectedDateWindow: DateWindow | null;
  disabled?: boolean;
}

export const dateIsNotInRange = (
  firstTimestamp: Date | null | undefined,
  lastTimestamp: Date | null | undefined,
  date: Date
): boolean => {
  if (!firstTimestamp || !lastTimestamp) {
    return true;
  }
  return date < firstTimestamp || date > lastTimestamp;
};

const getTimestampAtIndex = (
  table: DataTable | undefined,
  timestampAccessor: (row: DataRow) => Date | undefined,
  index: number
): Date | undefined => {
  if (table && table.length > 0 && index >= 0 && index < table.length) {
    return timestampAccessor(table[index]);
  }
  return undefined;
};

const initialStartDate = (selectedDateWindow: DateWindow | null): Date | undefined => {
  if (selectedDateWindow?.type === DateWindowTypes.DEFINED_START) {
    return selectedDateWindow.startDate;
  }

  return undefined;
};

export const DateWindowStartDateComponent = (props: DateWindowStartDateComponentProps): JSX.Element => {
  const [showError, setShowError] = React.useState<boolean>(false);
  const [errorText, setErrorText] = React.useState<React.ReactNode>(<br />);

  const def = simpleDefinition(dateGranularityFromTrunc(props.dateTrunc)).build();
  const timestampAccessor = buildTimestampAccessor(def);

  const dataTable = props.data;
  const firstTimestamp = getTimestampAtIndex(dataTable, timestampAccessor, 0);

  const [startDate, setStartDate] = React.useState<Date | undefined>(
    initialStartDate(props.selectedDateWindow) ?? firstTimestamp
  );

  React.useEffect(() => {
    props.onDateWindowChange(startDate ? createDefinedStartDateWindow(startDate) : null);
  }, [startDate]);

  const lastTimestamp = getTimestampAtIndex(dataTable, timestampAccessor, dataTable?.length - 1) ?? null;
  const handleChange = (value: Date | null) => {
    // date is parsed incorrectly or not in range
    setShowError(value === null || dateIsNotInRange(firstTimestamp, lastTimestamp, value));
    setErrorText(getErrorText(value));
  };

  const handleConfirm = (value: Date) => {
    if (!showError) {
      setStartDate(value);
    }
  };

  const getErrorText = (date: Date | null): React.ReactNode => {
    if (date === null && firstTimestamp) {
      return Messages.CustomStartDateError({
        dateExample: dateFormatterFactory(props.dateTrunc).slashFormat(firstTimestamp),
      });
    } else if (
      firstTimestamp &&
      lastTimestamp &&
      date !== null &&
      dateIsNotInRange(firstTimestamp, lastTimestamp, date)
    ) {
      return Messages.CustomStartDateRangeError({
        dateRangeString: dateFormatterFactory(props.dateTrunc).dateRangeSlashFormat(firstTimestamp, lastTimestamp),
      });
    }
    return <br />;
  };

  return (
    <DateInputField
      {...{
        dateInputFieldType: DateInputFieldType.Outline,
        isDatePickerDateDisabled: (date: Date) => dateIsNotInRange(firstTimestamp, lastTimestamp, date),
        hasDatePicker: true,
        testId: props.testId,
        onConfirm: handleConfirm,
        onChange: handleChange,
        selectedDate: startDate,
        message: errorText,
        valid: showError ? false : undefined,
        label: '',
        disabled: props.disabled,
      }}
    />
  );
};
